import { formatDate } from "@/lib/utils";
import { orderLineDetailText } from "@/lib/order-detail-text";
import type { Quotation, QuotationDetail } from '@/shared/schemas';
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

import { getCompanyConfig } from "@/config/company";
import { formatProductSpecs } from "@/lib/format-specs";

interface QuotationPrintTemplateProps {
    quotation: Quotation;
    config?: any;
    showImages?: boolean;
}

const parseSpecs = formatProductSpecs;

export default function QuotationPrintTemplate({ quotation, config, showImages = false }: QuotationPrintTemplateProps) {
    const c = getCompanyConfig(config);
    const details = (quotation.details || []) as QuotationDetail[];
    const createdDate = quotation.createdDate ? new Date(quotation.createdDate) : new Date();

    return (
        <div className="p-[10mm] text-black leading-relaxed text-[11px] bg-white w-full h-full mx-auto" style={{ fontFamily: "'Times New Roman', Times, serif" }} id="print-area">
            <style>{`
                .qt-table td, .qt-table th { border: 1px solid #000; }
                .qt-header-green { background: #c6efce !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .qt-yellow-bg { background: #ffff00 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            `}</style>

            {/* ========== TOP HEADER ========== */}
            <div className="flex justify-between items-start mb-4 gap-4">
                {/* Left: Logo */}
                <div className="w-[30%] flex items-start">
                    <img src={c.logoUrl} alt="Logo" className="w-[120px] h-auto max-h-[80px] object-contain flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>

                {/* Right: Company Info */}
                <div className="w-[70%] flex flex-col items-end text-right mt-2">
                    <div className="font-bold text-[13px] uppercase leading-tight mb-1 text-slate-800">{c.companyName}</div>
                    <div className="text-[10px] text-slate-600 uppercase font-medium">{c.companyNameEn || ''}</div>
                </div>
            </div>

            {/* ========== TITLE (CENTERED, NO QR) ========== */}
            <PrintDocumentTitle
                title={quotation.isNonMoq ? "BÁO GIÁ (NON-MOQ / CHẠY MẪU)" : "BÁO GIÁ"}
                subtitle={quotation.isNonMoq ? "Quotation (Non-MOQ / Sample Run)" : "Quotation"}
                docNo={`BG-${quotation.id.substring(0, 8)}`}
                date={createdDate}
                dateLabel="Ngày / Date"
            />

            {/* ========== PARTY INFO (Bên B - Khách hàng & Bên A - Đơn vị bán) ========== */}
            <PrintInfoGrid
                leftColumn={[
                    {
                        label: "Khách hàng",
                        labelEn: "The Buyer",
                        value: quotation.customer?.name || '',
                        valueClassName: "text-blue-900 font-bold",
                    },
                    {
                        label: "Địa chỉ",
                        labelEn: "Address",
                        value: quotation.deliveryAddress || quotation.customer?.address || '—',
                    },
                    {
                        label: "Điện thoại",
                        labelEn: "Tel",
                        value: quotation.phone || quotation.customer?.phone || '—',
                    },
                    {
                        label: "Người nhận",
                        labelEn: "Attn",
                        value: quotation.contactPerson || (quotation.customer as any)?.contactPerson || '—',
                    },
                ]}
                rightColumn={[
                    {
                        label: "Đơn vị bán",
                        labelEn: "The Seller",
                        value: c.companyName,
                        valueClassName: "text-slate-900 font-bold",
                    },
                    {
                        label: "Địa chỉ VP",
                        labelEn: "Office",
                        value: c.address1 || '—',
                    },
                    {
                        label: "Nhà máy",
                        labelEn: "Factory",
                        value: c.address2 || '—',
                    },
                    {
                        label: "Người gửi",
                        labelEn: "Sender",
                        value: quotation.creator?.fullName || (quotation as any).creatorName || (quotation as any).salesUser?.fullName || (quotation as any).senderName || '—',
                    },
                ]}
            />


            {/* ========== GREETING ========== */}
            <p className="mb-3 text-[10px] leading-[15px]">
                &nbsp;&nbsp;&nbsp;&nbsp;Xin cảm ơn Quý khách hàng đã quan tâm đến sản phẩm của Công ty, chúng tôi xin được gửi đến Quý khách hàng báo giá với nội dung sau:/
                <i>With reference to your valued enquiry, we are pleased to quote under the following terms and conditions:</i>
            </p>

            {/* ========== PRODUCT TABLE ========== */}
            {(() => {
                const foreignCurrency = quotation.currency || 'USD';
                const hasForeignCurrency = details.some((d: any) => Number(d.priceUSD) > 0);
                const hasVND = details.some((d: any) => Number(d.priceVND) > 0);
                const baseColCount = 7 + (showImages ? 1 : 0) + (hasForeignCurrency ? 1 : 0) + (hasVND ? 1 : 0);

                const normalized = details.map((d: any) => ({
                    ...d,
                    _sortDesc: (d.product?.process || d.process || '').toLowerCase().trim(),
                    // Sản phẩm khác mã nhưng trùng tên không được gộp chung ô (FB-9BRTHU)
                    _sortProduct: d.productId || d.product?.id || d.productCode || d.product?.productCode || '',
                    _sortName: (d.productName || d.productCode || '').toLowerCase().trim(),
                    _sortSpec: parseSpecs(d.specs, d.product),
                }));

                const sorted = normalized; // Giữ nguyên thứ tự gốc (FIFO) thay vì sort alphabetical làm lộn xộn

                const spanDetails: number[] = new Array(sorted.length).fill(0);
                let gi = 0;
                while (gi < sorted.length) {
                    let gj = gi + 1;
                    while (gj < sorted.length && sorted[gj]._sortDesc === sorted[gi]._sortDesc) gj++;
                    spanDetails[gi] = gj - gi;
                    gi = gj;
                }

                const spanProduct: number[] = new Array(sorted.length).fill(0);
                gi = 0;
                while (gi < sorted.length) {
                    let gj = gi + 1;
                    while (
                        gj < sorted.length &&
                        sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                        sorted[gj]._sortName === sorted[gi]._sortName &&
                        sorted[gj]._sortProduct === sorted[gi]._sortProduct &&
                        sorted[gj]._sortSpec === sorted[gi]._sortSpec
                    ) gj++;
                    spanProduct[gi] = gj - gi;
                    gi = gj;
                }

                const showImagesCol = !!showImages;
                const showUSDCol = hasForeignCurrency;
                const showVNDCol = hasVND;

                const remaining = 63 - (showImagesCol ? 10 : 0) - (showUSDCol ? 10 : 0) - (showVNDCol ? 11 : 0);
                const productWidth = `${Math.floor(remaining * 0.42)}%`;
                const descWidth = `${remaining - Math.floor(remaining * 0.42)}%`;

                let productCount = 0;

                return (
                    <table className="qt-table w-full border-collapse mb-4 text-[11px] table-fixed break-words">
                        <thead>
                            <tr className="qt-header-green font-bold text-center text-[11px]">
                                <th className="border border-black px-1 py-2" style={{ width: '4%' }}>Stt<br /><i className="font-normal">No.</i></th>
                                <th className="border border-black px-1 py-2" style={{ width: '10%' }}>Mã SP<br /><i className="font-normal">Code</i></th>
                                <th className="border border-black px-1 py-2 text-left" style={{ width: productWidth }}>Tên sản phẩm<br /><i className="font-normal">Product name</i></th>
                                <th className="border border-black px-1 py-2 text-left" style={{ width: descWidth }}>Chi tiết<br /><i className="font-normal">Description</i></th>
                                {showImagesCol && <th className="border border-black px-1 py-2" style={{ width: '10%' }}>Hình<br /><i className="font-normal">Image</i></th>}
                                <th className="border border-black px-1 py-2 whitespace-nowrap" style={{ width: '6%' }}>ĐVT<br /><i className="font-normal">Unit</i></th>
                                <th className="border border-black px-1 py-2 whitespace-nowrap" style={{ width: '8%' }}>{quotation.isNonMoq ? <>SL <span className="text-[9px] font-bold text-purple-700 block">(NON-MOQ)</span></> : <>SL<br /><i className="font-normal">Q'ty</i></>}</th>
                                {showUSDCol && <th className="border border-black px-1 py-2 whitespace-nowrap" style={{ width: '10%' }}>Đơn giá {foreignCurrency}<br /><i className="font-normal">Price {foreignCurrency}</i></th>}
                                {showVNDCol && <th className="border border-black px-1 py-2 whitespace-nowrap" style={{ width: '11%' }}>Đơn giá VND<br /><i className="font-normal">Price VND</i></th>}
                                <th className="border border-black px-1 py-2" style={{ width: '8%' }}>Ghi chú<br /><i className="font-normal">Notes</i></th>
                            </tr>
                        </thead>
                        <tbody>
                                {sorted.length === 0 ? (
                                    <tr>
                                        <td colSpan={baseColCount} className="border border-black p-4 italic text-slate-500 text-center">
                                            Chưa có sản phẩm nào
                                        </td>
                                    </tr>
                                ) : (
                                    sorted.map((item: any, index: number) => {
                                        const qty = Number(item.quantity) || 0;
                                        const priceVND = Number(item.priceVND) || 0;
                                        const priceUSD = Number(item.priceUSD) || 0;
                                        const productCode = item.productCode || item.product?.productCode || '';
                                        const productName = item.productName || '';
                                        const specsText = parseSpecs(item.specs || item.product?.specs || item.product?.packingSpec, item.product);
                                        // Chất liệu trước, rồi các công đoạn (dùng chung với bản in đơn hàng)
                                        const processText = orderLineDetailText(item);
                                        const notesCombined = item.notes || '';
                                        
                                        const showMergeProduct = spanProduct[index] > 0;
                                        const showMergeDetails = spanDetails[index] > 0;

                                        if (showMergeProduct) {
                                            productCount++;
                                        }

                                        return (
                                            <tr key={item.id || index} className="break-inside-avoid">
                                                {showMergeProduct && (
                                                    <td className="border border-black px-1 py-1.5 text-center align-middle" rowSpan={spanProduct[index]}>{productCount}</td>
                                                )}
                                                
                                                {showMergeProduct && (
                                                    <td rowSpan={spanProduct[index]} className="border border-black px-1 py-1.5 text-center font-mono align-top break-words">{productCode || '—'}</td>
                                                )}
                                                
                                                {showMergeProduct && (
                                                    <td rowSpan={spanProduct[index]} className="border border-black px-2 py-1.5 text-left align-top break-words">
                                                        {item.productGroup || item.product?.productType ? (
                                                            <span className="font-normal">{item.productGroup || item.product?.productType} - </span>
                                                        ) : ''}
                                                        <span className="font-bold">{productName}</span>
                                                        {specsText && <div className="text-slate-600 italic mt-0.5 font-normal">{specsText}</div>}
                                                    </td>
                                                )}
                                                
                                                {showMergeDetails && (
                                                    <td rowSpan={spanDetails[index]} className="border border-black px-1.5 py-1.5 text-left align-top whitespace-pre-wrap break-words">
                                                        <div className="font-semibold text-slate-900">{processText || 'In Offset Chuẩn ISO'}</div>
                                                        
                                                        {/* 1. HIỂN THỊ ĐA THÀNH PHẦN TỪ DATABASE COMPONENTS */}
                                                        {item.components && item.components.length > 0 && (
                                                            <div className="mt-1 pl-1.5 border-l-2 border-indigo-500/60 space-y-0.5">
                                                                {item.components.map((c: any, cIdx: number) => (
                                                                    <div key={cIdx} className="text-[10px] leading-tight">
                                                                        <span className="font-bold text-indigo-900">✦ {c.componentName || c.name || `Chi tiết #${cIdx + 1}`}:</span>{' '}
                                                                        <span className="text-slate-700">{c.material?.name || c.materialName || c.materialId || ''} {c.specs ? `(${c.specs})` : ''} {c.process ? `| ${c.process}` : ''}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* 2. HIỂN THỊ ĐA THÀNH PHẦN TỪ COST ESTIMATE KẾ THỪA */}
                                                        {item.costEstimate?.components && item.costEstimate.components.length > 0 && !item.components?.length && (
                                                            <div className="mt-1 pl-1.5 border-l-2 border-indigo-500/60 space-y-0.5">
                                                                {item.costEstimate.components.map((c: any, cIdx: number) => (
                                                                    <div key={cIdx} className="text-[10px] leading-tight">
                                                                        <span className="font-bold text-indigo-900">✦ {c.name || `Chi tiết #${cIdx + 1}`}:</span>{' '}
                                                                        <span className="text-slate-700">
                                                                            {c.materials?.map((m: any) => m.materialName).join(', ') || ''}{' '}
                                                                            {c.printColors ? `[${c.printColors}]` : ''}{' '}
                                                                            {c.routings?.map((r: any) => r.processName).join(' ➔ ')}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* 3. HIỂN THỊ SPECS DẠNG PIPE NẾU CÓ */}
                                                        {item.specs && !item.components?.length && !item.costEstimate?.components?.length && item.specs.includes('|') && (
                                                            <div className="mt-1 text-[9.5px] text-slate-700 space-y-0.5">
                                                                {item.specs.split('|').map((part: string, pIdx: number) => (
                                                                    <div key={pIdx} className="flex items-center gap-1">
                                                                        <span className="text-indigo-600 font-bold">•</span>
                                                                        <span>{part.trim()}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                                
                                                {showImages && showMergeProduct && (
                                                    <td rowSpan={spanProduct[index]} className="border border-black px-1 py-1 text-center align-top">
                                                        {(item.product?.image || item.image) ? (
                                                            <img src={item.product?.image || item.image} alt={productCode} className="w-[70px] h-[70px] object-contain mx-auto" />
                                                        ) : (
                                                            <span className="text-slate-300">—</span>
                                                        )}
                                                    </td>
                                                )}
                                                
                                                {showMergeProduct && (
                                                    <td className="border border-black px-1 py-1.5 text-center align-middle whitespace-nowrap" rowSpan={spanProduct[index]}>{item.unit || 'PCS'}</td>
                                                )}
                                                <td className="border border-black px-1 py-1.5 text-right font-medium whitespace-nowrap tabular-nums">{qty.toLocaleString('en-US')}</td>
                                                {hasForeignCurrency && <td className="border border-black px-1 py-1.5 text-right whitespace-nowrap tabular-nums">{priceUSD > 0 ? priceUSD.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '—'}</td>}
                                                {hasVND && <td className="border border-black px-1 py-1.5 text-right qt-yellow-bg font-bold whitespace-nowrap tabular-nums">{priceVND > 0 ? priceVND.toLocaleString('vi-VN') : '—'}</td>}
                                                <td className="border border-black px-1 py-1.5 break-words">{notesCombined || ''}</td>
                                            </tr>
                                        );
                                    })
                                )}
                        </tbody>
                    </table>
                );
            })()}

            {/* ========== NOTES / Ghi chú ========== */}
            <div className="mb-6 text-[10px] leading-[15px]">
                <h3 className="font-bold underline mb-2 text-[11px]">Ghi chú/ Notes:</h3>
                <div className="pl-6 space-y-2">
                    <p>1. Báo giá chưa bao gồm VAT./ <i>This quotation is excluding VAT.</i></p>
                    
                    {quotation.deposit && (
                        <p>2. Đặt cọc/ <i>Deposit:</i> <b>{quotation.deposit}</b></p>
                    )}

                    <div>
                        <p>{quotation.deposit ? '3' : '2'}. Phương thức thanh toán/ <i>Payment method:</i> {quotation.paymentTerm || c.defaultPaymentTerms || 'Theo thỏa thuận 2 bên'}/ <i>Follow agreement of both parties.</i></p>
                        <div className="pl-4 mt-1 space-y-0.5">
                            <p>Tài khoản (VND)/ <i>Bank account number in VND :</i> <b>{c.bankAccount}</b></p>
                            <p>Tên Ngân hàng/ <i>Bank name :</i> <b>{c.bankName}/ {c.bankBranch}</b></p>
                            <p>Tên tài khoản/ <i>Beneficiary :</i> <b>{c.beneficiary}</b></p>
                        </div>
                    </div>

                    <p>{quotation.deposit ? '4' : '3'}. Thời gian giao hàng/ <i>Time of delivery:</i> {quotation.deliveryTime || c.defaultDeliveryTerms || 'Theo thỏa thuận và kế hoạch của các bên'}/ <i>Follow agreement of both parties.</i></p>
                    
                    {(quotation.quantityTolerance || (quotation.customer as any)?.tolerancePercent) && (
                        <p>{quotation.deposit ? '5' : '4'}. Dung sai số lượng/ <i>Quantity Tolerance:</i> <b>{quotation.quantityTolerance || `+ ${(quotation.customer as any).tolerancePercent}%`}</b> (Sẽ không tính vào số lượng tính tiền)</p>
                    )}

                    {quotation.notes && (
                        <div className="mt-2 pt-2 border-t border-dashed border-slate-300">
                            <b>Các ghi chú khác:</b>
                            <p className="whitespace-pre-wrap">{quotation.notes}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ========== FOOTER SIGNATURES ========== */}
            <div className="flex justify-between mt-6 text-[11px] break-inside-avoid">
                {/* Left: Company signature */}
                <div className="text-center w-[45%]">
                    <div className="font-bold mb-20">{c.companyNameEn}</div>
                </div>

                {/* Right: Customer confirmation */}
                <div className="text-center w-[45%]">
                    <div className="font-bold mb-20 uppercase">
                        {quotation.customer?.name || 'KHÁCH HÀNG'}
                    </div>
                </div>
            </div>
        </div>
    );
}
